export type StorageArea = "local" | "session";

export const DISPOSABLE_STORAGE_PREFIXES = ["truevined-api-cache:", "truevined-api-miss:"];

const resolveStorage = (area: StorageArea): Storage | null => {
  if (typeof window === "undefined") return null;
  try {
    return (area === "session" ? window.sessionStorage : window.localStorage) ?? null;
  } catch {
    return null;
  }
};

export const isQuotaError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const { name, code } = error as { name?: string; code?: number };
  return (
    name === "QuotaExceededError" ||
    name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    code === 22 ||
    code === 1014
  );
};

export const listStorageKeys = (area: StorageArea = "local"): string[] => {
  const storage = resolveStorage(area);
  if (!storage) return [];
  try {
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key !== null) keys.push(key);
    }
    return keys;
  } catch {
    return [];
  }
};

export const evictDisposableEntries = (area: StorageArea = "local"): number => {
  const storage = resolveStorage(area);
  if (!storage) return 0;
  let removed = 0;
  listStorageKeys(area)
    .filter((key) => DISPOSABLE_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix)))
    .forEach((key) => {
      try {
        storage.removeItem(key);
        removed += 1;
      } catch {
        return;
      }
    });
  return removed;
};

export const safeGetItem = (key: string, area: StorageArea = "local"): string | null => {
  const storage = resolveStorage(area);
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

export const safeRemoveItem = (key: string, area: StorageArea = "local"): boolean => {
  const storage = resolveStorage(area);
  if (!storage) return false;
  try {
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

export const safeSetItem = (key: string, value: string, area: StorageArea = "local"): boolean => {
  const storage = resolveStorage(area);
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch (error) {
    if (!isQuotaError(error) || evictDisposableEntries(area) === 0) return false;
  }
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

export const safeGetJSON = <T>(key: string, area: StorageArea = "local"): T | null => {
  const raw = safeGetItem(key, area);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    safeRemoveItem(key, area);
    return null;
  }
};

export const safeSetJSON = (key: string, value: unknown, area: StorageArea = "local"): boolean => {
  let serialized: string;
  try {
    serialized = JSON.stringify(value);
  } catch {
    return false;
  }
  return safeSetItem(key, serialized, area);
};

export const safeClearStorage = (area: StorageArea): boolean => {
  const storage = resolveStorage(area);
  if (!storage) return false;
  try {
    storage.clear();
    return true;
  } catch {
    return false;
  }
};
